import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import maintenanceService, { MaintenanceStatus } from '../../services/maintenanceService';
import { useAuth } from '../../context/AuthContext';
import MaintenanceScreen from '../../pages/Maintenance/MaintenanceScreen';

interface Props {
  children: React.ReactNode;
}

const POLL_INTERVAL_MS = 60_000;

// Roles that can sign in normally while maintenance is active.
const BYPASS_ROLES = new Set(['super_admin', 'management']);

// Paths that should remain reachable even when maintenance is ON. Legal /
// public-information pages should stay accessible during downtime.
const ALWAYS_ACCESSIBLE_PATHS = new Set(['/legal', '/terms', '/privacy-policy', '/customer-policies', '/ai-health-terms']);

// Synchronously wipe auth tokens from localStorage so any in-flight code
// (interceptors, useEffects in already-mounted components) can no longer
// authenticate to the backend with the stale JWT.
function purgeAuthTokensSync() {
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('activeProfile');
    localStorage.removeItem('sr_accessToken');
    localStorage.removeItem('sr_refreshToken');
  } catch {
    // Storage quota / privacy mode — silently continue.
  }
}

const MaintenanceGate: React.FC<Props> = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState<MaintenanceStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const hasForcedLogoutRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await maintenanceService.getStatus();
      setStatus(data);
    } catch {
      // Backend unreachable — fail open so the app stays accessible.
      setStatus(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_INTERVAL_MS);
    const onFocus = () => fetchStatus();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchStatus]);

  // Reset the force-logout latch whenever maintenance flips back off.
  useEffect(() => {
    if (!status?.is_active) {
      hasForcedLogoutRef.current = false;
    }
  }, [status?.is_active]);

  const isBypassUser = isAuthenticated && user ? BYPASS_ROLES.has(user.role) : false;
  const isAlwaysAccessible = ALWAYS_ACCESSIBLE_PATHS.has(location.pathname);
  const params = new URLSearchParams(location.search);
  const hasBypassFlag = params.get('bypass') === 'admin' && location.pathname === '/login';

  // If maintenance is ON and a non-bypass user is currently authenticated,
  // synchronously clear their tokens so any other code that might fire a
  // request before the async logout resolves cannot reuse the JWT. We render
  // the maintenance screen immediately rather than waiting for AuthContext to
  // settle.
  useEffect(() => {
    if (!status?.is_active) return;
    if (!isAuthenticated) return;
    if (isBypassUser) return;
    if (hasForcedLogoutRef.current) return;

    hasForcedLogoutRef.current = true;
    purgeAuthTokensSync();
    logout().catch(() => undefined);
  }, [status?.is_active, isAuthenticated, isBypassUser, logout]);

  // While we wait for the very first status check, render children optimistically
  // (rather than a blank page). The gate will swap to the maintenance screen
  // once the response arrives — far better than 8s of nothing on a slow link.
  if (!loaded) {
    return <>{children}</>;
  }

  if (!status?.is_active) {
    return <>{children}</>;
  }

  if (isBypassUser || hasBypassFlag || isAlwaysAccessible) {
    return <>{children}</>;
  }

  // Authenticated non-bypass user mid-purge — render maintenance screen
  // immediately to avoid any flash of protected content. The auto-logout
  // effect above will finish clearing state on the next tick.
  return <MaintenanceScreen status={status} />;
};

export default MaintenanceGate;
